import React, { useEffect, useState } from "react";
import { Box, Dialog, Typography, useMediaQuery } from "@mui/material";
import { keyframes } from "@mui/system";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

/**
 * Blocking overlay covering the whole of placeOrder, from the tap to the
 * moment order detail takes over.
 *
 * placeOrder is not a fast mutation and cannot be made one: it charges the card
 * through QorCommerce inside the request, and that external call alone has been
 * measured at ~8s of the ~17s total. Until this existed the only feedback was a
 * spinner inside the button, which on a wait that long reads as "nothing
 * happened" — so customers tap again, hit back, or close the tab, any of which
 * risks a double charge or an order they never see confirmed.
 *
 * It has two phases, and the second one matters as much as the first. The
 * mutation returning is NOT the end of the work: onCompleted still refetches
 * the orders list (order detail renders by finding the id in it) before it
 * navigates. Closing the overlay when the mutation resolved dropped the
 * customer back onto the checkout page — cart, Place Order button and all —
 * for the length of that refetch, immediately after they had paid. Now the
 * overlay stays up and turns into a confirmation, so the last thing they see
 * on this screen is that it worked.
 */

// One element animates, and only while work is genuinely in flight — the
// guidance for infinite motion is that it must mean "busy" and nothing else.
const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.55; }
`;
// The confirmation lands rather than fades in: ease-out, and it plays once.
const pop = keyframes`
  from { transform: scale(0.7); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
`;

// Cumulative seconds at which each line takes over. The last one has no
// successor: if the gateway takes longer than this, it stays put rather than
// escalating into something alarming.
//
// The ORDER of these lines matters and is not cosmetic. placeOrder writes the
// order row, THEN charges the card, and only notifies the restaurant — push,
// email, and the NEW_ORDER subscription the restaurant app listens on — after
// the charge succeeds (backend resolvers/order.js). A declined card is rolled
// back to CANCELLED/FAILED and the restaurant never hears about it. So the
// restaurant genuinely comes last, and telling the customer otherwise would be
// a lie at exactly the moment they are most anxious.
//
// QOR settles inside placeOrder; STRIPE charges the card from the checkout
// page straight after it, under the same overlay, so the order is the same:
// prices, bank, authorise, restaurant. COD moves no money, and NMI creates the
// order as PAYMENT_PENDING and then redirects to pay — mentioning a bank on
// either of those would be wrong.
const CARD_STAGES = [
  { at: 0, label: "Checking your order and prices" },
  { at: 3, label: "Contacting your bank" },
  { at: 8, label: "Authorising the payment" },
  { at: 15, label: "Almost there — confirming with the restaurant" },
];
const CASH_STAGES = [
  { at: 0, label: "Checking your order and prices" },
  { at: 3, label: "Confirming with the restaurant" },
];
const REDIRECT_STAGES = [
  { at: 0, label: "Checking your order and prices" },
  { at: 3, label: "Taking you to secure payment" },
];
// The Stripe screen's own stages, now reached only on the way back from a
// method that had to leave the page to authorise. Placing the order is behind them, so these lines
// start at the bank — repeating "checking your order" there would read as the
// flow having looped back on itself. The work really is bank → authorise →
// restaurant: confirmPayment talks to the card network, /stripe/confirm-payment
// then marks the order paid and notifies the restaurant, and the orders refetch
// runs before the screen can change.
const PAY_STAGES = [
  { at: 0, label: "Contacting your bank" },
  { at: 4, label: "Authorising the payment" },
  { at: 10, label: "Almost there — confirming with the restaurant" },
];

function stagesFor(paymentMethod) {
  if (paymentMethod === "QOR" || paymentMethod === "STRIPE") return CARD_STAGES;
  if (paymentMethod === "STRIPE_PAY") return PAY_STAGES;
  if (paymentMethod === "NMI") return REDIRECT_STAGES;
  return CASH_STAGES;
}

/**
 * @param {"processing"|"success"|null} phase  null closes the overlay.
 * @param {string} paymentMethod  the STRING method ("QOR" | "COD" | …). Note the
 *   checkout screen holds this as an object — pass `paymentMethod.payment`.
 *   "STRIPE" is a checkout Place Order that places the order AND charges the
 *   card; "STRIPE_PAY" is the Stripe screen confirming a payment already
 *   authorised elsewhere. Both are charges and say so.
 */
export default function PaymentProcessing({
  phase,
  amount,
  currencySymbol = "$",
  paymentMethod,
  orderId,
  // Off while Stripe confirms: a method that authorises on its bank's own page
  // navigates away mid-charge, and "Leave site?" there would strand it.
  warnOnLeave = true,
}) {
  const [elapsed, setElapsed] = useState(0);
  // High-severity accessibility rule: honour the OS motion setting. The overlay
  // still appears and still updates its text — it just stops moving.
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const open = phase === "processing" || phase === "success";
  const done = phase === "success";
  const charging =
    paymentMethod === "QOR" || paymentMethod === "STRIPE" || paymentMethod === "STRIPE_PAY";

  useEffect(() => {
    if (phase !== "processing") {
      setElapsed(0);
      return undefined;
    }
    const started = Date.now();
    const timer = setInterval(() => setElapsed((Date.now() - started) / 1000), 500);
    return () => clearInterval(timer);
  }, [phase]);

  // Belt and braces on "do not close this window": the copy asks, and this
  // makes the browser ask too. Only while the charge is actually in flight —
  // once payment is confirmed the order exists, so leaving is harmless and
  // prompting them would just be nagging.
  useEffect(() => {
    if (phase !== "processing" || !warnOnLeave) return undefined;
    const warn = (e) => {
      e.preventDefault();
      // Browsers show their own wording; a non-empty value is what triggers it.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase, warnOnLeave]);

  const stages = stagesFor(paymentMethod);
  const stage = stages.filter((s) => elapsed >= s.at).pop() || stages[0];
  const stepNumber = stages.indexOf(stage) + 1;

  return (
    <Dialog
      open={open}
      // No onClose, and Escape disabled: this is the one moment in the app
      // where dismissing the dialog would be worse than waiting.
      disableEscapeKeyDown
      // Stripe's bank-authentication (3DS) challenge opens in its own iframe
      // outside this dialog while the overlay is up. MUI's focus trap would
      // pull focus back from it and the customer could not type the code.
      // Nothing in here is focusable anyway.
      disableEnforceFocus
      fullWidth
      maxWidth="xs"
      aria-labelledby="payment-processing-title"
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          overflow: "hidden",
        },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(25,25,25,0.72)" } }}
    >
      <Box
        role="status"
        aria-live="polite"
        aria-busy={!done}
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Box sx={{ position: "relative", width: 72, height: 72, mb: 2.5 }}>
          {done ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                backgroundColor: "success.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                animation: reduceMotion ? "none" : `${pop} 320ms ease-out`,
              }}
            >
              <CheckRoundedIcon sx={{ color: "#FFFFFF", fontSize: 40 }} />
            </Box>
          ) : (
            <>
              {/* The single moving element: a ring whose gap rotates.
                  Transform only, so it never triggers layout. */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: "3px solid",
                  borderColor: "rgba(255,48,8,0.18)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: "3px solid transparent",
                  borderTopColor: "primary.main",
                  animation: reduceMotion ? "none" : `${spin} 900ms linear infinite`,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LockOutlinedIcon sx={{ color: "primary.main", fontSize: 26 }} />
              </Box>
            </>
          )}
        </Box>

        <Typography
          id="payment-processing-title"
          sx={{ fontSize: 19, fontWeight: 800, color: "text.primary", mb: 0.5 }}
        >
          {done
            ? charging
              ? "Payment confirmed"
              : "Order placed"
            : charging
            ? "Processing your payment"
            : "Placing your order"}
        </Typography>

        {amount != null && (
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
            {currencySymbol}
            {Number(amount).toFixed(2)}
          </Typography>
        )}

        {done ? (
          <>
            <Typography sx={{ fontSize: 14, color: "text.primary", fontWeight: 700, maxWidth: 320 }}>
              Your order is placed{orderId ? ` — #${orderId}` : ""}
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: "text.secondary",
                mt: 0.75,
                maxWidth: 320,
                lineHeight: 1.5,
                animation: reduceMotion ? "none" : `${pulse} 1.8s ease-in-out infinite`,
              }}
            >
              Taking you to your order…
            </Typography>
          </>
        ) : (
          <>
            {/* Says what is happening now, and which step of how many — a wait
                that keeps changing reads as progress, a frozen one reads as a
                crash. */}
            <Typography
              sx={{
                fontSize: 14,
                color: "text.secondary",
                minHeight: 42,
                maxWidth: 320,
                lineHeight: 1.5,
                animation: reduceMotion ? "none" : `${pulse} 1.8s ease-in-out infinite`,
              }}
            >
              {stage.label}…
            </Typography>

            <Box sx={{ display: "flex", gap: 0.75, mt: 1.5, mb: 2.5 }}>
              {stages.map((s, i) => (
                <Box
                  key={s.at}
                  sx={{
                    width: i < stepNumber ? 22 : 8,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: i < stepNumber ? "primary.main" : "rgba(25,25,25,0.12)",
                    transition: "width 300ms ease-out, background-color 300ms ease-out",
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                width: "100%",
                borderRadius: 2,
                px: 2,
                py: 1.25,
                backgroundColor: "rgba(255,48,8,0.07)",
              }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: "primary.main", lineHeight: 1.45 }}
              >
                Please don't close this window or press back
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}
